import { Button, Typography } from '@material-tailwind/react';
import { TrophyIcon } from '@heroicons/react/24/outline';
import FloatingBarFrame from './FloatingBarFrame';
import { useState } from 'react';
import { createShareUrl } from '../../../utils/free-trial/utils';
import {
  copulaParticle,
  getErrorMessages,
  handleCopyClipBoard,
  removeSurname,
  subjectParticle,
} from '../../../api/utils/util-func';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import { createShareTokenWithConsent } from '../../../api/utils/free-trial/api';
import { FreeTrialStatusItem } from '../../../constants/types/free-trial/type';
import { useFreeTrialConfig } from '../../../hooks/useFreeTrialConfig';

interface CompletedFreeTrialFloatingBarProps {
  isVisible: boolean;
  isScrolling: boolean;
  freeTrialStatus: FreeTrialStatusItem;
}

const CompletedFreeTrialFloatingBar = ({
  isVisible,
  isScrolling,
  freeTrialStatus,
}: CompletedFreeTrialFloatingBarProps) => {
  const dispatch = useAppDispatch();

  const config = useFreeTrialConfig();

  const { childName } = freeTrialStatus;

  const [isCreatingShareLink, setIsCreatingShareLink] = useState(false);

  const openKakaoLink = () => {
    window.open(config.FREE_TRIAL_KAKAO, '_blank');
  };

  const onClickRecommend = async () => {
    if (isCreatingShareLink) return;

    try {
      setIsCreatingShareLink(true);

      const { shareToken } = await createShareTokenWithConsent(
        freeTrialStatus.linkToken,
      );

      const shareUrl = createShareUrl(shareToken);

      const result = await handleCopyClipBoard(shareUrl);

      dispatch(
        openAlert({
          message: result
            ? '공유 링크가 복사되었습니다.'
            : '공유 링크 복사에 실패했습니다.',
        }),
      );
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
      console.error('공유 링크 생성 실패', error);
    } finally {
      setIsCreatingShareLink(false);
    }
  };

  const floatingBarStateClass = isScrolling
    ? 'gap-1.5 rounded-[18px] p-1'
    : 'gap-2.5 rounded-[20px] p-2 lg:gap-4 lg:px-4 lg:py-2.5';

  const leftStateClass = isScrolling ? 'gap-0' : 'gap-2.5';

  const badgeStateClass = isScrolling
    ? 'h-9 w-9'
    : 'h-[42px] w-[42px] lg:h-14 lg:w-14';

  const trophyIconStateClass = isScrolling
    ? 'h-5 w-5'
    : 'h-5 w-5 lg:h-6 lg:w-6';

  const actionButtonStateClass = isScrolling
    ? 'h-10 px-4 text-[12px]'
    : 'h-[60px] px-4 text-[13px] lg:h-14 lg:px-6 lg:text-[14px]';

  const completedMessageStateClass = isScrolling
    ? 'max-w-0 opacity-0'
    : 'max-w-[230px] opacity-100';

  return (
    <FloatingBarFrame
      isVisible={isVisible}
      isScrolling={isScrolling}
      compactWidthClass="w-[140px]"
    >
      <div
        className={`flex w-full items-center border border-[#E5BE00] bg-[#FFD928] shadow-[0_8px_24px_rgba(88,57,180,0.18)] transition-all duration-300 ease-out ${floatingBarStateClass} lg:w-max lg:rounded-full`}
      >
        {/* 왼쪽 상태 영역 */}
        <div
          className={`flex min-w-0 flex-1 items-center transition-all duration-300 ${leftStateClass}`}
        >
          {/* 완료 뱃지 */}
          <div
            className={`flex shrink-0 items-center justify-center rounded-[18px] border border-[#E5BE00] bg-white text-[#E5BE00] transition-all duration-300 lg:rounded-full ${badgeStateClass}`}
          >
            <TrophyIcon
              className={`stroke-[2.2] transition-all duration-300 ${trophyIconStateClass}`}
            />
          </div>

          {/* 완료 문구 */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-out ${completedMessageStateClass}`}
          >
            <Typography className="whitespace-nowrap text-[12px] font-extrabold leading-[1.3] text-gray-900 lg:text-[13px]">
              <span className="block sm:hidden">
                {subjectParticle(copulaParticle(removeSurname(childName)))}
              </span>
              <span className="block sm:hidden">첫 암송을 성공했어요!</span>

              <span className="hidden sm:inline">
                {subjectParticle(copulaParticle(removeSurname(childName)))} 첫
                암송을 성공했어요!
              </span>
            </Typography>
          </div>
        </div>

        {/* 오른쪽 액션 영역 */}
        <div className="ml-auto flex shrink-0 items-center gap-2 lg:gap-3">
          <Button
            ripple={false}
            onClick={openKakaoLink}
            className={`shrink-0 rounded-[18px] bg-[#F1EDFF] py-0 font-bold normal-case text-[#6737F4] shadow-none transition-all duration-300 hover:bg-[#E9E2FF] hover:shadow-none focus:shadow-none lg:rounded-full ${actionButtonStateClass}`}
          >
            {isScrolling ? (
              '상담'
            ) : (
              <>
                <span className="flex flex-col items-center leading-[1.15] lg:hidden">
                  <span>정규 과정</span>
                  <span>상담</span>
                </span>

                <span className="hidden whitespace-nowrap lg:inline">
                  정규 과정 상담
                </span>
              </>
            )}
          </Button>

          <Button
            ripple={false}
            disabled={isCreatingShareLink}
            onClick={onClickRecommend}
            className={`group relative shrink-0 overflow-visible rounded-[18px] bg-[#6737F4] py-0 font-bold normal-case text-white shadow-none transition-all duration-300 hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none disabled:bg-[#9B8EC8] disabled:text-white lg:rounded-full ${actionButtonStateClass}`}
          >
            {!isScrolling && !isCreatingShareLink && (
              <>
                {/* ring pulse */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-[3px] block animate-[free-trial-ring_2.8s_ease-in-out_infinite] rounded-[18px] border-2 border-[#A98EFF]/50 lg:rounded-full"
                />

                {/* shine */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 block overflow-hidden rounded-[18px] lg:rounded-full"
                >
                  <span className="absolute -inset-y-4 -left-16 w-10 rotate-[18deg] animate-[free-trial-shine_2.6s_ease-in-out_infinite] bg-white/50 blur-md" />
                </span>
              </>
            )}

            <span className="relative z-10">
              {isCreatingShareLink ? (
                '복사 중...'
              ) : isScrolling ? (
                '추천'
              ) : (
                <>
                  <span className="flex flex-col items-center leading-[1.15] lg:hidden">
                    <span>친구에게</span>
                    <span>추천</span>
                  </span>

                  <span className="hidden whitespace-nowrap lg:inline">
                    친구에게 추천
                  </span>
                </>
              )}
            </span>
          </Button>
        </div>
      </div>
    </FloatingBarFrame>
  );
};

export default CompletedFreeTrialFloatingBar;
